import { jsx as _jsx } from "react/jsx-runtime";
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useRef } from 'react';
import { addBreadcrumb, setTag, captureException } from '@sentry/react';
import { generateCorrelationId } from '../../../../helpers/correlationId';
/**
 * PayPalButtonWrapper - Wrapper component for the official @paypal/react-paypal-js library
 *
 * This component provides backwards compatibility with the old @repeatgg/react-paypal-button-v2 API
 * while using the official PayPal React SDK under the hood.
 *
 */
export const PayPalButtonWrapper = ({ options, createOrder, onApprove, onCancel, onError, onButtonReady, onSuccess, }) => {
    // Store correlation ID to persist across createOrder and handleSuccess
    const correlationIdRef = useRef(null);
    // Wrap createOrder with telemetry
    const instrumentedCreateOrder = async (data, actions) => {
        // Generate a new correlation ID at the start of each order flow
        const correlationId = generateCorrelationId();
        correlationIdRef.current = correlationId;
        // Set Sentry tag for this transaction
        setTag('correlationId', correlationId);
        addBreadcrumb({
            category: 'paypal',
            message: 'PayPal order create started',
            level: 'info',
            data: { correlationId },
        });
        try {
            // Call the original createOrder callback
            const paypalOrderId = await (createOrder === null || createOrder === void 0 ? void 0 : createOrder(data, actions));
            if (!paypalOrderId) {
                throw new Error('createOrder returned undefined');
            }
            addBreadcrumb({
                category: 'paypal',
                message: 'PayPal order created successfully',
                level: 'info',
                data: { correlationId, paypalOrderId },
            });
            return paypalOrderId;
        }
        catch (error) {
            setTag('paypalError', 'createOrder');
            captureException(error, {
                extra: {
                    correlationId,
                    phase: 'createOrder',
                    message: 'Failed to create PayPal order',
                },
            });
            throw error;
        }
    };
    // Map legacy onSuccess to onApprove for backwards compatibility
    // The old @repeatgg/react-paypal-button-v2 called: onSuccess(captureDetails, approvalData)
    // We need to match this signature exactly
    const handleApprove = onSuccess
        ? async (data, actions) => {
            var _a, _b, _c, _d, _e, _f;
            const correlationId = correlationIdRef.current;
            addBreadcrumb({
                category: 'paypal',
                message: 'PayPal order approved, capturing payment',
                level: 'info',
                data: { correlationId, orderID: data.orderID },
            });
            try {
                // Capture the order to get full transaction details including capture ID
                const captureDetails = await ((_a = actions.order) === null || _a === void 0 ? void 0 : _a.capture());
                if (!captureDetails) {
                    throw new Error('Failed to capture PayPal order');
                }
                addBreadcrumb({
                    category: 'paypal',
                    message: 'PayPal payment captured successfully',
                    level: 'info',
                    data: {
                        correlationId,
                        orderID: data.orderID,
                        captureId: (_f = (_e = (_d = (_c = (_b = captureDetails.purchase_units) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.payments) === null || _d === void 0 ? void 0 : _d.captures) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.id,
                    },
                });
                addBreadcrumb({
                    category: 'paypal',
                    message: 'Calling onSuccess callback',
                    level: 'info',
                    data: { correlationId, orderID: data.orderID },
                });
                // Call onSuccess with the same signature as the old package:
                // onSuccess(captureDetails, approvalData)
                return onSuccess(captureDetails, data);
            }
            catch (error) {
                setTag('paypalError', 'capture');
                captureException(error, {
                    extra: {
                        correlationId,
                        orderID: data.orderID,
                        phase: 'capture',
                        message: 'Failed to capture or process PayPal payment',
                    },
                });
                throw error;
            }
        }
        : onApprove;
    // Wrap onError with additional telemetry
    const handleError = (error) => {
        const correlationId = correlationIdRef.current;
        addBreadcrumb({
            category: 'paypal',
            message: 'PayPal SDK error occurred',
            level: 'error',
            data: { correlationId, error: JSON.stringify(error) },
        });
        setTag('paypalError', 'sdkError');
        captureException(new Error('PayPal SDK error'), {
            extra: {
                correlationId,
                phase: 'sdkError',
                sdkError: error,
            },
        });
        onError === null || onError === void 0 ? void 0 : onError(error);
    };
    return (_jsx(PayPalScriptProvider, { options: {
            clientId: options.clientId,
            currency: 'USD',
        }, children: _jsx(PayPalButtons, { createOrder: instrumentedCreateOrder, onApprove: handleApprove, onCancel: onCancel, onError: handleError, onInit: onButtonReady, style: {
                layout: 'vertical',
                color: 'gold',
                shape: 'rect',
                label: 'paypal',
            } }) }));
};
